// ─── Unit definitions ────────────────────────────────────────────────────────

export interface UnitDef {
  symbol: string;
  label: string;
  plural?: string; // plural display form (only for countable units that change)
  category: "measurable" | "countable" | "descriptive";
  dimension?: "mass" | "volume";
  toBase?: number; // factor to convert to base unit (g for mass, ml for volume)
}

export const UNITS: Record<string, UnitDef> = {
  // measurable — symbols don't change with quantity
  g: { symbol: "g", label: "g", category: "measurable", dimension: "mass", toBase: 1 },
  kg: { symbol: "kg", label: "kg", category: "measurable", dimension: "mass", toBase: 1000 },
  ml: { symbol: "ml", label: "ml", category: "measurable", dimension: "volume", toBase: 1 },
  lt: { symbol: "lt", label: "lt", category: "measurable", dimension: "volume", toBase: 1000 },
  // countable — pluralize when quantity > 1
  cda: { symbol: "cda", label: "cda", plural: "cdas", category: "countable" },
  cdta: { symbol: "cdta", label: "cdta", plural: "cdtas", category: "countable" },
  unidad: { symbol: "unidad", label: "unidad", plural: "unidades", category: "countable" },
  diente: { symbol: "diente", label: "diente", plural: "dientes", category: "countable" },
  hoja: { symbol: "hoja", label: "hoja", plural: "hojas", category: "countable" },
  lata: { symbol: "lata", label: "lata", plural: "latas", category: "countable" },
  feta: { symbol: "feta", label: "feta", plural: "fetas", category: "countable" },
  atado: { symbol: "atado", label: "atado", plural: "atados", category: "countable" },
  paquete: { symbol: "paquete", label: "paquete", plural: "paquetes", category: "countable" },
  pizca: { symbol: "pizca", label: "pizca", plural: "pizcas", category: "countable" },
  bife: { symbol: "bife", label: "bife", plural: "bifes", category: "countable" },
  filete: { symbol: "filete", label: "filete", plural: "filetes", category: "countable" },
  suprema: { symbol: "suprema", label: "suprema", plural: "supremas", category: "countable" },
  medallón: { symbol: "medallón", label: "medallón", plural: "medallones", category: "countable" },
  rama: { symbol: "rama", label: "rama", plural: "ramas", category: "countable" },
  rodaja: { symbol: "rodaja", label: "rodaja", plural: "rodajas", category: "countable" },
  gajo: { symbol: "gajo", label: "gajo", plural: "gajos", category: "countable" },
  tira: { symbol: "tira", label: "tira", plural: "tiras", category: "countable" },
  sobre: { symbol: "sobre", label: "sobre", plural: "sobres", category: "countable" },
  taza: { symbol: "taza", label: "taza", plural: "tazas", category: "countable" },
  disco: { symbol: "disco", label: "disco", plural: "discos", category: "countable" },
  chorrito: { symbol: "chorrito", label: "chorrito", plural: "chorritos", category: "countable" },
  cubo: { symbol: "cubo", label: "cubo", plural: "cubos", category: "countable" },
  pote: { symbol: "pote", label: "pote", plural: "potes", category: "countable" },
  // descriptive — quantity must be null, no pluralization
  "a gusto": { symbol: "a gusto", label: "a gusto", category: "descriptive" },
  "cantidad necesaria": {
    symbol: "cantidad necesaria",
    label: "cantidad necesaria",
    category: "descriptive",
  },
};

export const VALID_UNIT_SYMBOLS = new Set(Object.keys(UNITS));

// ─── Grouped options for <select> / <optgroup> ──────────────────────────────

export interface UnitOptionGroup {
  label: string;
  options: { value: string; label: string }[];
}

export const UNIT_OPTION_GROUPS: UnitOptionGroup[] = [
  {
    label: "Peso",
    options: [
      { value: "g", label: "g (gramos)" },
      { value: "kg", label: "kg (kilos)" },
    ],
  },
  {
    label: "Volumen",
    options: [
      { value: "ml", label: "ml (mililitros)" },
      { value: "lt", label: "lt (litros)" },
    ],
  },
  {
    label: "Contable",
    options: [
      { value: "cda", label: "cda (cucharada)" },
      { value: "cdta", label: "cdta (cucharadita)" },
      { value: "unidad", label: "unidad" },
      { value: "diente", label: "diente" },
      { value: "hoja", label: "hoja" },
      { value: "lata", label: "lata" },
      { value: "feta", label: "feta" },
      { value: "atado", label: "atado" },
      { value: "paquete", label: "paquete" },
      { value: "pizca", label: "pizca" },
      { value: "bife", label: "bife" },
      { value: "filete", label: "filete" },
      { value: "suprema", label: "suprema" },
      { value: "medallón", label: "medallón" },
      { value: "rama", label: "rama" },
      { value: "rodaja", label: "rodaja" },
      { value: "gajo", label: "gajo" },
      { value: "tira", label: "tira" },
      { value: "sobre", label: "sobre" },
      { value: "taza", label: "taza" },
      { value: "disco", label: "disco" },
      { value: "chorrito", label: "chorrito" },
      { value: "cubo", label: "cubo" },
      { value: "pote", label: "pote" },
    ],
  },
  {
    label: "Descriptivo",
    options: [
      { value: "a gusto", label: "a gusto" },
      { value: "cantidad necesaria", label: "cantidad necesaria" },
    ],
  },
];

// ─── Universal volume conversions ───────────────────────────────────────────
// Exact ml equivalents for volume-like countable units.
// These are universal (not per-ingredient) and allow merging with ml/lt.

export const UNIVERSAL_VOLUME: Record<string, number> = {
  cdta: 5,
  cda: 15,
  taza: 240,
};

// ─── Conversion range type ──────────────────────────────────────────────────

export interface ConversionRange {
  min: number;
  max: number;
  toUnit: "g" | "ml";
}

// ─── Pluralization ──────────────────────────────────────────────────────────

/** Return the display form of a unit, pluralized when quantity > 1 (Spanish convention: fractions < 1 use singular). */
export function displayUnit(unit: string, quantity?: number | null): string {
  const def = UNITS[unit];
  if (!def) return unit;
  if (def.plural && quantity != null && (Math.abs(quantity) > 1 || quantity === 0))
    return def.plural;
  return def.label;
}

// ─── Conversion utilities ───────────────────────────────────────────────────

/** Convert a quantity to its base unit (g for mass, ml for volume). Returns null for non-measurable. */
export function toBaseQuantity(
  qty: number,
  unit: string,
): { quantity: number; dimension: "mass" | "volume" } | null {
  const def = UNITS[unit];
  if (!def || def.category !== "measurable" || !def.dimension || !def.toBase) return null;
  return { quantity: qty * def.toBase, dimension: def.dimension };
}

/** Pick the best human-friendly unit for a base quantity in a given dimension. */
export function humanizeQuantity(
  baseQty: number,
  dimension: "mass" | "volume",
): { quantity: number; unit: string } {
  if (dimension === "mass") {
    return baseQty >= 1000
      ? { quantity: baseQty / 1000, unit: "kg" }
      : { quantity: baseQty, unit: "g" };
  }
  // volume
  return baseQty >= 1000
    ? { quantity: baseQty / 1000, unit: "lt" }
    : { quantity: baseQty, unit: "ml" };
}

/** Pick the best human-friendly unit for a range of base quantities. */
export function humanizeRange(
  min: number,
  max: number,
  dimension: "mass" | "volume" = "mass",
): { min: number; max: number; unit: string } {
  const ref = Math.max(Math.abs(min), Math.abs(max));
  if (dimension === "volume") {
    if (ref >= 1000) return { min: min / 1000, max: max / 1000, unit: "lt" };
    return { min, max, unit: "ml" };
  }
  if (ref >= 1000) return { min: min / 1000, max: max / 1000, unit: "kg" };
  return { min, max, unit: "g" };
}

/**
 * Determine the aggregation key for a grocery item.
 * - Measurable units → `ingredientId:mass` or `ingredientId:volume`
 * - Volume-like countable (cda/cdta/taza) → `ingredientId:volume` (universal conversion)
 * - Countable units with a per-ingredient conversion → `ingredientId:mass`
 * - Countable units without conversion → `ingredientId:unitSymbol`
 * - Descriptive / non-numeric → `ingredientId:desc`
 */
export function getAggregationKey(
  ingredientId: string,
  unit: string | null,
  isNumeric: boolean,
  conversions?: Map<string, ConversionRange>,
  densities?: Map<string, number>,
): string {
  if (!isNumeric) return `${ingredientId}:desc`;

  const def = UNITS[unit ?? ""];
  const hasDensity = densities?.has(ingredientId) ?? false;

  if (def?.category === "measurable" && def.dimension) {
    return `${ingredientId}:${hasDensity ? "measurable" : def.dimension}`;
  }

  // Volume-like countable (cda, cdta, taza) → merge into volume (or measurable with density)
  if (unit && UNIVERSAL_VOLUME[unit]) {
    return `${ingredientId}:${hasDensity ? "measurable" : "volume"}`;
  }

  // Countable with a per-ingredient conversion → merge into target dimension (or measurable)
  if (conversions && unit) {
    const range = conversions.get(`${ingredientId}:${unit}`);
    if (range) {
      if (hasDensity) return `${ingredientId}:measurable`;
      return `${ingredientId}:${range.toUnit === "ml" ? "volume" : "mass"}`;
    }
  }

  return `${ingredientId}:${unit ?? ""}`;
}

// ─── Spoon humanization ────────────────────────────────────────────────────

/** Common fractions recognized as "clean" for display purposes. */
export const CLEAN_FRACTIONS: readonly [number, string][] = [
  [1 / 4, "¼"],
  [1 / 3, "⅓"],
  [1 / 2, "½"],
  [2 / 3, "⅔"],
  [3 / 4, "¾"],
];

function isCleanNumber(n: number): boolean {
  if (Number.isInteger(n)) return true;
  const frac = n - Math.floor(n);
  return CLEAN_FRACTIONS.some(([v]) => Math.abs(frac - v) < 0.001);
}

/**
 * Spoon-step units ordered largest → smallest.
 * Each entry: [unit symbol, ml equivalent].
 */
const SPOON_STEPS: [string, number][] = [
  ["taza", 240],
  ["cda", 15],
  ["cdta", 5],
];

/**
 * Humanize a ml quantity into the best spoon-family unit.
 * Only applicable when the original unit was a spoon-like unit (cdta, cda, taza).
 *
 * Priority cascade (like responsive breakpoints — first match wins):
 * 1. taza ≥1, or clean sub-taza fraction (½, ⅓, ¼, ¾)
 * 2. Exact cda (integer)
 * 3. Exact cdta (integer, ≤12) — prefer 5 cdta over 1⅔ cda, but not 29 cdta over 9⅔ cda
 * 4. Clean fractional cda (e.g. 9⅔) — easy to eyeball in a tablespoon
 * 5. Fallback: taza ≥½ taza (120ml), cda ≥1 cda (15ml), else cdta
 */
export function humanizeSpoon(ml: number): { quantity: number; unit: string } {
  const [tazaStep, cdaStep, cdtaStep] = SPOON_STEPS;
  const tazaQty = ml / tazaStep![1];
  const cdaQty = ml / cdaStep![1];
  const cdtaQty = ml / cdtaStep![1];

  // 1. taza: ≥1 always, or clean sub-taza fractions (½, ⅓, ¼, ¾)
  if (tazaQty >= 1) return { quantity: tazaQty, unit: "taza" };
  if (isCleanNumber(tazaQty) && tazaQty > 0) return { quantity: tazaQty, unit: "taza" };
  // 2. Exact cda (integer) before cdta
  if (Number.isInteger(cdaQty) && cdaQty >= 1) return { quantity: cdaQty, unit: "cda" };
  // 3. Exact cdta — prefer over fractional cda for small amounts (5 cdta > 1⅔ cda)
  //    Cap at 12; beyond that, fractional cda is more practical (e.g. 9⅔ cda > 29 cdta)
  if (Number.isInteger(cdtaQty) && cdtaQty >= 1 && cdtaQty <= 12)
    return { quantity: cdtaQty, unit: "cdta" };
  // 4. Clean fractional cda (e.g. 9⅔ cda) — easy to eyeball in a tablespoon
  if (isCleanNumber(cdaQty) && cdaQty >= 1) return { quantity: cdaQty, unit: "cda" };
  // Fallback for non-clean amounts: prefer the largest readable unit.
  // ≥½ taza shows as fractional taza; ≥1 cda as decimal cda; else cdta.
  const taza = SPOON_STEPS[0]!;
  const cda = SPOON_STEPS[1]!;
  const cdta = SPOON_STEPS[2]!;
  if (ml >= taza[1] / 2) return { quantity: ml / taza[1], unit: taza[0] };
  if (ml >= cda[1]) return { quantity: ml / cda[1], unit: cda[0] };
  return { quantity: ml / cdta[1], unit: cdta[0] };
}

/**
 * Convert a quantity + unit to base grams, considering both measurable units
 * and ingredient-level conversions for countable units.
 * Returns null if no conversion is possible.
 */
export function toGrams(
  qty: number,
  unit: string,
  ingredientId: string,
  conversions?: Map<string, ConversionRange>,
  densities?: Map<string, number>,
): number | null {
  // Measurable mass units
  const base = toBaseQuantity(qty, unit);
  if (base && base.dimension === "mass") return base.quantity;

  // Countable with ingredient conversion (only g-targeted)
  if (conversions) {
    const range = conversions.get(`${ingredientId}:${unit}`);
    if (range && range.toUnit === "g") return qty * range.min;
  }

  // Cross-dimension via density: convert volume → grams
  const density = densities?.get(ingredientId);
  if (density) {
    const ml = toMl(qty, unit, ingredientId, conversions); // no density to avoid recursion
    if (ml != null) return ml * density;
  }

  return null;
}

/**
 * Convert a quantity + unit to base ml, considering both measurable units
 * and ingredient-level conversions for countable units.
 * Returns null if no conversion is possible.
 */
export function toMl(
  qty: number,
  unit: string,
  ingredientId: string,
  conversions?: Map<string, ConversionRange>,
  densities?: Map<string, number>,
): number | null {
  // Measurable volume units
  const base = toBaseQuantity(qty, unit);
  if (base && base.dimension === "volume") return base.quantity;

  // Universal volume (cda, cdta, taza)
  const univMl = UNIVERSAL_VOLUME[unit];
  if (univMl) return qty * univMl;

  // Countable with ingredient conversion (only ml-targeted)
  if (conversions) {
    const range = conversions.get(`${ingredientId}:${unit}`);
    if (range && range.toUnit === "ml") return qty * range.min;
  }

  // Cross-dimension via density: convert mass → ml
  const density = densities?.get(ingredientId);
  if (density) {
    const grams = toGrams(qty, unit, ingredientId, conversions); // no density to avoid recursion
    if (grams != null) return grams / density;
  }

  return null;
}
